import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { storage } from "../Storage";
import { INotifyEnum, notify } from "../toaster";

import { ISessionKeys } from "@/models";
import { IRoleType } from "@/models/role";

import { config, setIsShowEditProfileModal } from "@/store/slices/config";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { useRedirectAfterLogin } from "./useRedirectAfterLogin";
import { usePatientProfile } from "./usePatientProfile";
import { useGooglePlaces } from "./useGooglePlaces";
import { durgapurCoordinates } from "../Config";

export const useGeolocation = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const { redirectAfterLogin } = useAppSelector(config);
  const handleRedirectAfterLogin = useRedirectAfterLogin();
  const { fetchPatientProfileDetail } = usePatientProfile();
  const dispatch = useAppDispatch();

  const { getAddressFromCoordinates } = useGooglePlaces({
    apiKey: import.meta.env.VITE_APP_GOOGLE_PLACES_API_KEY,
  });

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const errorGetLocationAccess = (error: GeolocationPositionError) => {
    setIsLoading(false);
    let errorMessage = "";

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage =
          "Please enable location permissions in your browser settings to continue.";
        console.log("User denied the request for Geolocation.");
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = "Unable to detect your location. Please try again.";
        console.log("Location information is unavailable.");
        break;
      case error.TIMEOUT:
        errorMessage = "Location request timed out. Please try again.";
        console.log("The request to get user location timed out.");
        break;
      default:
        errorMessage =
          "An error occurred while getting your location. Please try again.";
        console.log("An unknown error occurred.");
        break;
    }

    notify(INotifyEnum.ERROR, errorMessage);
  };

  const successGetLocationAccess = async (
    position: GeolocationPosition,
    closeHandler?: () => void
  ) => {
    try {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      await getAddressFromCoordinates({
        lat: latitude,
        lng: longitude,
      });
      storage.setSessionToken(
        ISessionKeys.LOCATION,
        JSON.stringify({ latitude, longitude })
      );
      if (closeHandler) {
        closeHandler();
      }
      if (redirectAfterLogin?.isRedirect && roleType !== IRoleType.GUEST) {
        handleRedirectAfterLogin();
      } else {
        navigate("/find-doctor", { state: { navItem: 1 } });
      }
      if (roleType !== IRoleType.GUEST) {
        const profileDetail = await fetchPatientProfileDetail();
        if (
          !profileDetail?.name ||
          !profileDetail?.age ||
          !profileDetail?.gender
        ) {
          dispatch(setIsShowEditProfileModal(true));
        }
      }
    } catch (error) {
      console.error("Error processing location:", error);
      storage.setSessionToken(
        ISessionKeys.LOCATION,
        JSON.stringify({
          latitude: durgapurCoordinates.latitude,
          longitude: durgapurCoordinates.longitude,
        })
      );
      if (closeHandler) {
        closeHandler();
      }
      if (redirectAfterLogin?.isRedirect && roleType !== IRoleType.GUEST) {
        handleRedirectAfterLogin();
      } else {
        navigate("/find-doctor", { state: { navItem: 1 } });
      }
      if (roleType !== IRoleType.GUEST) {
        const profileDetail = await fetchPatientProfileDetail();
        if (
          !profileDetail?.name ||
          !profileDetail?.age ||
          !profileDetail?.gender
        ) {
          dispatch(setIsShowEditProfileModal(true));
        }
        setIsLoading(false);
      }
    }
  };

  const getLocation = (closeHandler?: () => void) => {
    setIsLoading(true);

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => successGetLocationAccess(position, closeHandler),
        errorGetLocationAccess,
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      setIsLoading(false);
      console.error("Geolocation is not supported by this browser.");
    }
  };

  return {
    getLocation,
    isLoading,
  };
};
